import {
  defineStore,
  storeToRefs,
} from 'pinia';
import api from '@/services/api';
import prices_model from '../models/prices_model';
import prices_filters_model from '../models/prices_filters_model';
import prices_producto_model from '../models/prices_producto_model';

import initial_delivery_model from '@/shared/models/delivery_model.js';
import { getParamsUrl } from '../prices_utils.js';
import { useAuthStore } from '../../auth/auth_store';
import { Modules } from '@/enum/modules';
import elemento_grupo_service from '@/services/elemento_grupo_service';
import { generarElemento } from '@/shared/generators/genericGenerator';
import { useElementoBaseStore } from '@/stores/elemento_base/elemento_base_store';
import { useConfiguracionStore } from '@/stores/configuracion-general.js';
import { ElementoBase } from '@/enum/elemento_base';
import { Proceso } from '@/enum/proceso';
import { Role } from '@/enum/roles_enum.js';
import { cantidadesIguales } from '@/shared/utils/cantidad_unidad_venta.js';

const allowedProperties = [
  'CodigoSolicitud',
  'CodigoEstado',
  'FechaEstado',
  'CodigoCliente',
  'ObservacionesSolicitud',
  'ObservacionesComerciales',
  'Observaciones',
  'Notas',
  'CodigoUnidadVenta',
  'Referencia',
  'CodigoMoneda',
  'ObservacionEstado',
  'CostoFinanciero',
  'TasaDolarCompra',
  'TasaDolarVenta',
  'CodigoMotivoRechazo',
  'CodigoUsabilidad',
  'CotizacionCantidad',
  'CotizacionAdjunto',
  'CotizacionProducto',
  'CotizacionElemento',
  'Moneda',
  'Usabilidad',
  'MotivoRechazo',
  'CodigoUsuarioCotizacion',
  'FechaCotizacion',
  'CotizacionEntregas',
];
function filterPayload(payload) {
  return allowedProperties.reduce((acc, key) => {
    if (Object.prototype.hasOwnProperty.call(payload, key)) {
      acc[key] = payload[key];
    }
    return acc;
  }, {});
}
export const usePricesStore = defineStore('pricesStore', {
  state: () => ({
    cotizacion: JSON.parse(JSON.stringify(prices_model)),
    filters: { ...prices_filters_model },
    validarCotizacionCampos: {
      Cliente: true,
      Referencia: true,
      MedidaEje: true,
      MedidaDesarrollo: true,
      AnchoMaterial: true,
      CantidadEtiquetas: true,
      MargenSuperior: true,
      FamiliaMaterial: true,
      GapEje: true,
      GapDesarrollo: true,
      Forma: true,
      EtiquetasDesarrollo: true,
      EtiquetasEje: true,
      TasaDolarCompra: true,
      TasaDolarVenta: true,
      Solapa: true,
      Sellado: true,
      Base: true,
      AltoTotal: true,
      CodigoMoneda: true,
      CodigoUnidadVenta: true,
    },
    // Nombres de los campos que fallaron en la última validación, para poder decirle al
    // usuario qué falta en vez de un "revisar formulario" a secas.
    camposCotizacionInvalidos: [],
    cotizaciones: [],
    cotizacionesConCalculos: [],
    page: 1,
    pageCalculos: 1,
    isModalPreProposalVisible: false,
    prePropuestaComercial: [],
  }),
  getters: {
    primerMaterialImpresion: (state) => {
      const procesos = state.cotizacion?.CotizacionElemento?.[0]?.CotizacionElementoProceso ?? [];
      if (!procesos.length) return null;

      const tieneValidaAnchoEnProceso = (p) =>
        (p?.Proceso?.ProcesoElementoTipoCategoriaMaterial ?? []).some(
          (x) => x?.CategoriaMaterial?.ValidaAnchoMaterial === true,
        );

      const materialValidaAncho = (m) =>
        m?.Material?.MaterialTipo?.MaterialCategoria?.ValidaAnchoMaterial === true;

      const tieneDims = (m) => m?.Ancho !== null || m?.Largo !== null;

      const candidatos = procesos
        .map((p) => {
          if (!tieneValidaAnchoEnProceso(p)) return null;

          const mats = p?.CotizacionElementoProcesoMateriales ?? [];
          const hasMats = mats.length > 0;
          const hasDims = mats.some(tieneDims);

          const costoOrdenMin =
            (p?.Proceso?.ProcesoRecurso ?? [])
              .map((pr) => pr?.Costo?.Orden)
              .filter((v) => typeof v === 'number')
              .sort((a, b) => a - b)[0] ?? Number.MAX_SAFE_INTEGER;

          return {
            proceso: p,
            mats,
            hasMats,
            hasDims,
            costoOrdenMin,
            itemProceso: p?.ItemProceso ?? Number.MAX_SAFE_INTEGER,
          };
        })
        .filter(Boolean);

      if (!candidatos.length) return null;

      candidatos.sort((a, b) => {
        if (a.hasMats !== b.hasMats) return a.hasMats ? -1 : 1;
        if (a.hasDims !== b.hasDims) return a.hasDims ? -1 : 1;
        if (a.costoOrdenMin !== b.costoOrdenMin) return a.costoOrdenMin - b.costoOrdenMin;
        return a.itemProceso - b.itemProceso;
      });

      const ganador = candidatos[0];
      if (!ganador?.mats?.length) return null;

      const m1 = ganador.mats.find((m) => materialValidaAncho(m) && tieneDims(m));
      if (m1) return m1;

      const m2 = ganador.mats.find(materialValidaAncho);
      if (m2) return m2;

      return null;
    },
    procesoValidaAncho: (state) => {
      const procesos = state.cotizacion?.CotizacionElemento?.[0]?.CotizacionElementoProceso ?? [];
      const tieneValidaAnchoEnProceso = (p) =>
        (p?.Proceso?.ProcesoElementoTipoCategoriaMaterial ?? []).some(
          (x) => x?.CategoriaMaterial?.ValidaAnchoMaterial === true,
        );
      return procesos.find(tieneValidaAnchoEnProceso) || null;
    },
    validarSumatoriaCantidades: (state) => {
      const cabecera = state.cotizacion?.CotizacionCantidad || [];
      const productos = state.cotizacion?.CotizacionProducto || [];

      if (productos.length === 0) return cabecera.map(() => true);

      return cabecera.map((qtyCabecera, index) => {
        const totalCabecera = Number(qtyCabecera.Cantidad || 0);

        const sumatoriaDetalle = productos.reduce((acc, prod) => {
          const qtyDetalle = prod.CotizacionProductoCantidad?.[index]?.Cantidad || 0;
          return acc + Number(qtyDetalle);
        }, 0);

        return cantidadesIguales(totalCabecera, sumatoriaDetalle);
      });
    },
    cantidadesValidadas: (state) => {
      return state.validarSumatoriaCantidades.every((isValid) => isValid === true);
    },
  },
  actions: {
    // async setPriceInitialProcess(CodigoElementoTipo) {
    //   const elementoTipoStore = useElementoTipoStore()
    //   const procesosPorDefecto = await elementoTipoStore.findAndReturnProcesosPorDefectoHTTP({
    //     CodigoElementoTipo,
    //     Modulo: 'cotizacion'
    //   })

    //   if (procesosPorDefecto?.length) {
    //     const procesos = procesosPorDefecto.map((proceso, index) => {
    //       const item = {
    //         ...CotizacionElementoProceso,
    //         ItemProceso: index + 1,
    //         CodigoSeccion: proceso.CodigoSeccion,
    //         Proceso: { ...proceso.Proceso },
    //         Recurso: { ...proceso.Recurso }
    //       }

    //       if (proceso.Proceso.Material) {
    //         item.CotizacionElementoProcesoMateriales = proceso.MaterialPorDefecto.map((m) => ({
    //           ...CotizacionElementoProcesoMateriales,
    //           Material: { ...m.Material }
    //         }))
    //       }

    //       return item
    //     })

    //     this.price.CotizacionElemento[0].CotizacionElementoProceso = procesos
    //   }
    // },
    resetValidaciones() {
      Object.keys(this.validarCotizacionCampos).forEach((key) => {
        this.validarCotizacionCampos[key] = true;
      });
      this.camposCotizacionInvalidos = [];
    },
    async vincularProductosTerminadosHTTP(codigoCotizacion, payload, itemCantidadAprobada) {
      try {
        const response = await api.patch(`/cotizacion/${codigoCotizacion}/vincular-productos`, {
          productos: payload,
          itemCantidadObjetivo: itemCantidadAprobada,
        });

        if (response.status === 200 || response.status === 201) {
          return response.data;
        }
        return null;
      } catch (error) {
        console.error('Error al vincular productos terminados:', error);
        throw error;
      }
    },
    validarCamposCotizacion() {
      const elementoBaseStore = useElementoBaseStore();
      const { elementoBaseConfig } = storeToRefs(elementoBaseStore);

      // TODO: implementar validacion en todos los elementos
      const primerElemento = this.cotizacion.CotizacionElemento[0];
      if (!primerElemento) {
        for (const key in this.validarCotizacionCampos) {
          this.validarCotizacionCampos[key] = false;
        }
        return this.checkPropertiesValidation();
      }

      this.validarCotizacionCampos = {
        AltoTotal: true,
        AnchoMaterial: true,
        Base: true,
        CantidadEtiquetas: true,
        Cliente: !!this.cotizacion.Cliente.Descripcion,
        CodigoMoneda: !!this.cotizacion.Moneda?.Codigo,
        EtiquetasDesarrollo: true,
        EtiquetasEje: true,
        FamiliaMaterial: !!(
          primerElemento.MateriaPrimaFamilia && primerElemento.MateriaPrimaFamilia.Codigo
        ),
        Forma: true,
        GapDesarrollo: true,
        GapEje: true,
        MargenSuperior: true,
        MedidaDesarrollo: true,
        MedidaEje: true,
        Referencia: !!this.cotizacion.Referencia,
        Sellado: true,
        Solapa: true,
        TasaDolarCompra: true,
        TasaDolarVenta: true,
        CodigoUnidadVenta: !!this.cotizacion.CodigoUnidadVenta,
      };

      const codigoElementoBase = primerElemento?.ElementoTipo?.CodigoElementoBase;

      // COTI-507: extrusion no usa los campos del elemento base (suaje, medidas, familia
      // de material), asi que se saltean todas esas validaciones y en su lugar se validan
      // los datos del film resultante, que son los que el cotizador necesita.
      if (codigoElementoBase === ElementoBase.EXTRUSION) {
        this.validarCotizacionCampos.FamiliaMaterial = true;
        this.validarCamposExtrusion(primerElemento);
      } else {
        if (elementoBaseConfig.value.campos.mostrarMedidaEje) {
          this.validarCotizacionCampos.MedidaEje = !!primerElemento.MedidaEje;
        } else {
          this.validarCotizacionCampos.MedidaEje = true;
        }

        if (elementoBaseConfig.value.campos.mostrarMedidaDesarrollo) {
          this.validarCotizacionCampos.MedidaDesarrollo = !!primerElemento.MedidaDesarrollo;
        } else {
          this.validarCotizacionCampos.MedidaDesarrollo = true;
        }

        if (elementoBaseConfig.value.campos.mostrarAnchoMaterial) {
          this.validarCotizacionCampos.AnchoMaterial = !!primerElemento.AnchoMaterial;
        } else {
          this.validarCotizacionCampos.AnchoMaterial = true;
        }

        if (
          ![
            ElementoBase.TERMO_ENCOGIBLE,
            ElementoBase.CAJA,
            ElementoBase.CONSUMIBLE,
            ElementoBase.FLEXIBLE,
            ElementoBase.BOLSAS_FUELLE,
            ElementoBase.BOLSAS_BOX,
            // COTI-502: en offset el campo de familia de material no se muestra, así que
            // exigirlo dejaba el formulario inválido sin nada que el usuario pudiera corregir.
            ElementoBase.OFFSET,
          ].includes(codigoElementoBase)
        ) {
          this.validarCotizacionCampos.FamiliaMaterial =
            !!primerElemento.MateriaPrimaFamilia?.Codigo;
        } else if ([ElementoBase.BOLSAS_BOX].includes(codigoElementoBase)) {
          this.validarCotizacionCampos.Sellado = !!primerElemento.Sellado;
          this.validarCotizacionCampos.Base = !!primerElemento.Base;
        } else if ([ElementoBase.BOLSAS_FUELLE].includes(codigoElementoBase)) {
          this.validarCotizacionCampos.Base = !!primerElemento.Base;
        } else {
          this.validarCotizacionCampos.FamiliaMaterial = true;
        }

        if (elementoBaseConfig.value.campos.mostrarMargenSuperior) {
          if (codigoElementoBase === ElementoBase.CAJA) {
            const v = primerElemento?.MargenSuperior;
            const n = Number(v);
            this.validarCotizacionCampos.MargenSuperior =
              v !== null && v !== undefined && v !== '' && !Number.isNaN(n) && n >= 0;
          } else {
            this.validarCotizacionCampos.MargenSuperior = !!primerElemento.MargenSuperior;
          }
        } else {
          this.validarCotizacionCampos.MargenSuperior = true;
        }
      }

      if (this.cotizacion.Moneda?.Codigo !== 1) {
        this.validarCotizacionCampos.TasaDolarCompra =
          !!this.cotizacion.TasaDolarCompra && this.cotizacion.TasaDolarCompra > 0;
        this.validarCotizacionCampos.TasaDolarVenta =
          !!this.cotizacion.TasaDolarVenta && this.cotizacion.TasaDolarVenta > 0;
      } else {
        this.validarCotizacionCampos.TasaDolarCompra = true;
        this.validarCotizacionCampos.TasaDolarVenta = true;
      }

      return this.checkPropertiesValidation();
    },
    /**
     * Datos del film resultante que el cotizador necesita cuando el elemento se resuelve
     * por dimensionamiento continuo. Sin el gramaje —cargado directo o derivado de
     * Densidad x Espesor, según calculo_material_extrusion— no puede derivar el largo del
     * pedido y falla con "desarrolloMm debe ser > 0".
     *
     * El ancho del elemento no se valida como campo propio: en extrusión no se carga a
     * mano, sale de la sumatoria de los anchos de las bobinas del proceso. Lo que se exige
     * son las bobinas.
     */
    validarCamposExtrusion(elemento) {
      const { calculoMaterialExtrusion } = useConfiguracionStore();
      const esCalculoPorGramaje = calculoMaterialExtrusion === 'gramaje';

      const procesos = elemento.CotizacionElementoProceso || [];
      const procesosExtrusion = procesos.filter(
        (p) => (p.Proceso?.Codigo ?? p.CodigoProceso) === Proceso.EXTRUSION,
      );
      // Todavía no se agregó el proceso de extrusión: no hay film del que hablar.
      if (procesosExtrusion.length === 0) return;

      // Mismo criterio que esSoloExtrusion en el cotizador: si la extrusión convive con
      // otros procesos, el elemento se dimensiona por pliego físico y el gramaje del film
      // no entra en el cálculo, así que no hay por qué exigirlo.
      if (procesos.length !== procesosExtrusion.length) return;

      const cargado = (valor) =>
        valor !== null && valor !== undefined && valor !== '' && Number(valor) > 0;
      const todos = (predicado) => procesosExtrusion.every(predicado);

      this.validarCotizacionCampos.MaterialResultante = todos((p) =>
        cargado(p.CodigoMaterialResultante ?? p.MaterialResultante?.Codigo),
      );
      // De acá sale el ancho del material, que el cotizador exige > 0.
      this.validarCotizacionCampos.BobinasExtrusion = todos((p) =>
        (p.ElementoProcesoBobinas || []).some((bobina) => cargado(bobina?.Ancho)),
      );

      if (esCalculoPorGramaje) {
        this.validarCotizacionCampos.GramajeResultante = todos((p) => cargado(p.GramajeResultante));
      } else {
        this.validarCotizacionCampos.EspesorResultante = todos((p) => cargado(p.EspesorResultante));
        this.validarCotizacionCampos.DensidadResultante = todos((p) =>
          cargado(p.DensidadResultante),
        );
      }
    },
    checkPropertiesValidation() {
      this.camposCotizacionInvalidos = Object.keys(this.validarCotizacionCampos).filter(
        (property) => !this.validarCotizacionCampos[property],
      );

      if (this.camposCotizacionInvalidos.length > 0) {
        console.log(`Campos inválidos: ${this.camposCotizacionInvalidos.join(', ')}`);
        return false;
      }
      return true;
    },
    async findAllCotizacionesHTTP(params) {
      const authStore = useAuthStore();
      const usuario = authStore.usuario();
      const esEjecutivoDeVentas = usuario.roles.some(
        (rol) => rol.Codigo === Role.EJECUTIVO_DE_VENTAS,
      );

      if (esEjecutivoDeVentas) {
        this.filters.CodigoUsuarioCreacion = usuario.Codigo;
      }
      if (this.filters.EstadoCotizacion && this.filters.EstadoCotizacion.Codigo !== null) {
        this.filters.AllCotizaciones = false;
      }
      const result = await api.get(
        `/cotizacion?${getParamsUrl({ ...this.filters, page: +this.page, ...params })}`,
      );
      if (result && result.data && result.data.length > 0) {
        this.cotizaciones.push(...result.data);
        this.setPage(this.page + 1);
        return true;
      }
      return false;
    },
    async findCotizacionesConCalculosHTTP(params) {
      const authStore = useAuthStore();
      const usuario = authStore.usuario();

      const esEjecutivoDeVentas = usuario.roles.some((r) => r.Codigo === Role.EJECUTIVO_DE_VENTAS);
      const esPresupuestista = usuario.roles.some((r) => r.Codigo === Role.PRESUPUESTISTA);

      const filtrosUsuario = {};
      if (esEjecutivoDeVentas) {
        filtrosUsuario.CodigoUsuarioCreacion = usuario.Codigo;
      } else if (esPresupuestista) {
        if (usuario.UsuariosRelacionados && usuario.UsuariosRelacionados.length > 0) {
          filtrosUsuario.CodigoUsuarioCreacion = [usuario.Codigo, ...usuario.UsuariosRelacionados];
        }
      }

      const filtrosCompletos = {
        ...filtrosUsuario,
        AllCotizaciones: false,
        CodigosEstados: [2, 3, 7],
      };

      const result = await api.get(
        `/cotizacion?${getParamsUrl({ ...filtrosCompletos, page: +this.pageCalculos, ...params })}`,
      );

      if (result && result.data && result.data.length > 0) {
        this.cotizacionesConCalculos.push(...result.data);
        this.pageCalculos++;
        return true;
      }
      return false;
    },
    async findCotizacionPorCodigoHTTP(codigo) {
      try {
        const result = await api.get(`/cotizacion/${codigo}`);
        if (result && result.data) {
          this.cotizacion = {
            ...result.data,
            CotizacionProducto: result.data.CotizacionProducto || [],
            CotizacionEntregas: result.data.CotizacionEntregas || [],
          };
        }
      } catch (error) {
        console.error('Error en findCotizacionPorCodigoHTTP:', error);
      }
    },
    // async cotizarHTTP() {
    //   this.cotizacion.CostoFinanciero = 1
    //   delete this.cotizacion.Solicitud
    //   const response = await api.put(`/cotizacion/cotizar/${this.cotizacion.Codigo}`, this.cotizacion)
    //   if (response.status === 200 || response.status === 201) {
    //     this.cotizacion = {
    //       ...response.data,
    //       CotizacionProducto: response.data.CotizacionProducto || [],
    //       CotizacionEntregas: response.data.CotizacionEntregas || []
    //     }
    //   }
    // },
    async newPriceNuevoHTTP() {
      this.cotizacion.CostoFinanciero = 1;
      delete this.cotizacion.Solicitud;
      delete this.cotizacion.CotizacionCantidadCostos;
      const payloadToSend = filterPayload(this.cotizacion);
      const response = await api.put(
        `/cotizacion/cotizar-nuevo/${this.cotizacion.Codigo}`,
        payloadToSend,
      );
      if (response.status === 200 || response.status === 201) {
        this.cotizacion = {
          ...response.data,
          CotizacionProducto: response.data.CotizacionProducto || [],
          CotizacionEntregas: response.data.CotizacionEntregas || [],
        };
      }
    },
    async createPriceHTTP() {
      const payload = { ...this.cotizacion };
      // 2) Lista de keys que el DTO NO acepta
      const forbidden = [
        'Contacto',
        'EmailContacto',
        'CodigoCondicionPago',
        'CodigoValidez',
        'CondicionPago',
        'Validez',
        'CotizacionElementoProceso',
        // 'CotizacionElementoTinta',
        'CotizacionCantidadCosto',
      ];

      // 3) Elimínalas
      forbidden.forEach((key) => {
        if (key in payload) delete payload[key];
      });

      const response = await api.post('/cotizacion/nuevaCotizacion', payload);
      if (response.status === 200 || response.status === 201) {
        this.cotizacion = {
          ...response.data,
          CotizacionProducto: response.data.CotizacionProducto || [],
          CotizacionEntregas: response.data.CotizacionEntregas || [],
        };
        return response;
      }
    },
    async updateSolicitud() {
      const response = await api.post(`/cotizacion/actualizar-solicitud`, this.cotizacion);
      if (response.status === 200 || response.status === 201) {
        return response;
      }
    },
    async getDiferenciasEtHTTP(codigo) {
      const response = await api.get(`/cotizacion/${codigo}/diferencias-et`);
      return response.data;
    },
    async actualizarEtHTTP(codigo, { actualizarTodos, camposAActualizar }) {
      const response = await api.patch(`/cotizacion/${codigo}/actualizar-et`, {
        actualizarTodos,
        camposAActualizar,
      });
      return response.data;
    },
    // async gestionarYcotizar() {
    //   this.cotizacion.CostoFinanciero = 1
    //   delete this.cotizacion.Solicitud
    //   const response = await api.put(`cotizacion/gestionarYcotizar/${this.cotizacion.Codigo}`, this.cotizacion)
    //   if (response.status === 200 || response.status === 201) {
    //     this.cotizacion = {
    //       ...response.data,
    //       CotizacionProducto: response.data.CotizacionProducto || [],
    //       CotizacionEntregas: response.data.CotizacionEntregas || []
    //     }
    //   }
    // },
    async firstCotizaciones() {
      this.setPage(1);
      this.emptyList();
      const authStore = useAuthStore();
      const usuario = authStore.usuario();

      const esEjecutivoDeVentas = usuario.roles.some((r) => r.Codigo === 1);
      const esPresupuestista = usuario.roles.some((r) => r.Codigo === 2);

      if (esEjecutivoDeVentas) {
        this.filters.CodigoUsuarioCreacion = usuario.Codigo;
      } else if (esPresupuestista) {
        if (usuario.UsuariosRelacionados && usuario.UsuariosRelacionados.length > 0) {
          this.filters.CodigoUsuarioCreacion = [usuario.Codigo, ...usuario.UsuariosRelacionados];
        } else {
          this.filters.CodigoUsuarioCreacion = [];
        }
      }
      await this.findAllCotizacionesHTTP();
    },
    addProducto() {
      if (!this.cotizacion.CotizacionProducto) {
        this.cotizacion.CotizacionProducto = [];
      }
      const nuevoProducto = { ...prices_producto_model };
      const esUnicoProducto = this.cotizacion.CotizacionProducto.length === 0;

      nuevoProducto.CotizacionProductoCantidad = (this.cotizacion.CotizacionCantidad || []).map(
        (qty) => ({
          Cantidad: esUnicoProducto ? qty.Cantidad : null,
          ItemCantidad: qty.ItemCantidad,
        }),
      );

      this.cotizacion.CotizacionProducto.push(nuevoProducto);
    },
    autoCompletarCantidadUnica(indexCabecera) {
      const productos = this.cotizacion.CotizacionProducto;
      if (productos && productos.length === 1) {
        const cantidadCabecera = this.cotizacion.CotizacionCantidad[indexCabecera].Cantidad;
        if (
          productos[0].CotizacionProductoCantidad &&
          productos[0].CotizacionProductoCantidad[indexCabecera]
        ) {
          productos[0].CotizacionProductoCantidad[indexCabecera].Cantidad = cantidadCabecera;
        }
      }
    },
    updateProducto(data) {
      this.cotizacion.CotizacionProducto[data.index] = data.producto;

      if (this.cotizacion.CotizacionProducto.length === 1) {
        const cantidadesDetalle = data.producto.CotizacionProductoCantidad || [];

        cantidadesDetalle.forEach((detalleQty, index) => {
          if (this.cotizacion.CotizacionCantidad[index] && detalleQty.Cantidad !== null) {
            this.cotizacion.CotizacionCantidad[index].Cantidad = detalleQty.Cantidad;
          }
        });
      }
    },
    removeProducto(index) {
      const productos = this.cotizacion.CotizacionProducto;
      if (productos && productos.length) {
        productos.splice(index, 1);
        this.cotizacion.CotizacionProducto = productos;
      }
    },
    resetCotizacion() {
      this.cotizacion = JSON.parse(JSON.stringify(prices_model));
    },
    setPage(page) {
      this.page = page;
    },
    emptyList() {
      this.cotizaciones = [];
    },
    resetCotizacionesConCalculos() {
      this.cotizacionesConCalculos = [];
      this.pageCalculos = 1;
    },
    setFilters(newFilters) {
      this.filters = { ...newFilters };
    },
    resetFilter() {
      this.filters = { ...prices_filters_model };
    },
    createPrePropuestaComercial() {
      const propuestaCotizacionCantidad = {
        CodigoCotizacion: this.cotizacion.Codigo,
        items: [
          ...JSON.parse(
            JSON.stringify(
              this.cotizacion.CotizacionCantidad.map((i, Orden) => {
                return {
                  ...i,
                  Orden,
                  Cotizacion: JSON.parse(JSON.stringify(this.cotizacion)),
                };
              }),
            ),
          ),
        ],
      };

      // this.cotizacion.CotizacionElemento.forEach( elemento => {
      //   elemento.CotizacionElementoProceso
      //     .filter( (proceso) => proceso.Adicional == true )
      //     .forEach( (proceso, Orden) => {
      //       propuestaCotizacionCantidad.items.push({
      //         Cantidad: proceso.Cantidad,
      //         CodigoCotizacion: this.cotizacion.Codigo,
      //         ComisionMonto: 0,
      //         ComisionMontoOriginal: 0,
      //         ComisionOriginal: 0,
      //         CostoTotal: 0,
      //         CostoTotalOriginal: 0,
      //         Orden: Orden + 1000,
      //         PrecioTotal: 0,
      //         PrecioTotalOriginal: 0,
      //         PrecioUnidadVenta: 0,
      //         PrecioUnidadVentaModificado: 0
      //       })
      //     })
      // })

      this.prePropuestaComercial.push(propuestaCotizacionCantidad);
    },
    async createFromETHTTP(codigoET, payload) {
      const response = await api.post(
        `/cotizacion/from-especificacion-tecnica/${codigoET}`,
        payload,
      );
      return response.data || response;
    },
    setProductTypeInPrice(typeSelected) {
      this.productTypeSelected = typeSelected;
      this.cotizacion.CotizacionElemento[0].ElementoTipo = { ...typeSelected };
      localStorage.setItem('productTypeSelected', JSON.stringify(typeSelected));
    },
    addEntrega() {
      if (!this.cotizacion.CotizacionEntregas) {
        this.cotizacion.CotizacionEntregas = [];
      }
      this.cotizacion.CotizacionEntregas &&
        this.cotizacion.CotizacionEntregas.push({
          ...initial_delivery_model,
          CodigoCotizacion: null,
        });
    },
    removeEntrega() {
      const deliveries = this.cotizacion.CotizacionEntregas;
      if (deliveries && deliveries.length) {
        this.cotizacion.CotizacionEntregas = deliveries.slice(0, deliveries.length - 1);
      }
    },
    updateEntrega({ delivery, index }) {
      if (this.cotizacion.CotizacionEntregas && index >= 0) {
        const deliveries = [...this.cotizacion.CotizacionEntregas];
        const { Cantidad, Fecha } = delivery;
        deliveries[index] = { ...this.cotizacion.CotizacionEntregas[index], Cantidad, Fecha };
        this.cotizacion.CotizacionEntregas = deliveries;
      }
    },
    async setDefaults(codigo) {
      const elementosDefaults = await elemento_grupo_service.getGrupoDefaults(
        codigo,
        Modules.COTIZACION,
      );
      elementosDefaults.forEach((elementoDefault, index) => {
        const elementoGenerated = generarElemento(Modules.COTIZACION, elementoDefault, index);
        this.cotizacion.CotizacionElemento[index].CotizacionElementoProceso =
          elementoGenerated.CotizacionElementoProceso;
      });
    },
  },
});
